"use client";

import { Award, Home, ShieldCheck, Users } from "lucide-react";
import Link from "next/link";
import Footer from "./components/Footer";
import HeroSlider from "./components/HeroSlider";
import Facility from "./components/Facility";
import ContactBanner from "./components/ContactBanner";
import Reveal from "./components/Reveal";
import ClinicSlider from "./components/ClinicSlider";
import ServiceAccordion from "./components/ServiceAccordion";
import ArticleCarousel from "./components/ArticleCarousel";
import GoogleRating from "./components/GoogleRating";
import TestimonialCarousel from "./components/TestimonialCarousel";
import DoctorSearch from "./components/DoctorSearch";

/* PENGUMUMAN OPERASIONAL BERBATAS WAKTU.

   Dua komponen, bukan satu, karena keduanya hidup di tempat berbeda
   dan mati di waktu berbeda: stripnya hilang sendiri pada tanggal
   yang disetel di app/data/pengumuman.js, section-nya tetap tinggal
   dan hanya berganti kalimat. Menggabungkannya berarti nanti harus
   dibongkar lagi. */
import StripPengumuman from "./components/StripPengumuman";
import PengumumanReservasiBPJS from "./components/PengumumanReservasiBPJS";

import { useLanguage } from "./i18n/LanguageProvider";

/* ============================================================
   SHADOW SCALE

   Rumus jangkauan shadow:
     jangkauan samping = (blur / 2) - spread
     jangkauan bawah   = offsetY + (blur / 2) - spread

   Tiga layer per token, meniru cara benda nyata menghasilkan bayangan:
     1. contact  - garis rapat, bikin elemen menempel ke permukaan
     2. mid      - mengisi celah antara contact dan ambient
     3. ambient  - halo lebar, bikin elemen terasa mengambang

   Warna dasarnya plum gelap rgba(18,22,26) = #12161a, BUKAN hitam.
   Shadow hitam di atas background plum terbaca keabu-abuan.
   ============================================================ */

const SHADOW_HERO =
  "shadow-[0_1px_2px_rgba(18,22,26,0.4),0_8px_16px_-6px_rgba(18,22,26,0.45),0_32px_64px_-16px_rgba(18,22,26,0.55)]";

const SHADOW_CARD =
  "shadow-[0_1px_2px_rgba(18,22,26,0.3),0_6px_12px_-4px_rgba(18,22,26,0.35),0_20px_40px_-12px_rgba(18,22,26,0.4)]";

/* Data telepon Klinik Kirana sudah pindah ke ClinicSlider.jsx bersama
   data klinik lainnya, jadi konstanta di sini dihapus. Menyimpan nomor
   yang sama di dua file adalah cara paling umum nomor jadi menyimpang. */

/* Motif batik diagonal, dipakai sebagai aksen di satu section saja. */
const BATIK =
  "bg-[linear-gradient(45deg,#ffffff10_25%,transparent_25%,transparent_75%,#ffffff10_75%,#ffffff10),linear-gradient(-45deg,#ffffff10_25%,transparent_25%,transparent_75%,#ffffff10_75%,#ffffff10)] [background-size:80px_80px]";

/* WAVE_MASK dan WAVE_STYLE dihapus.

   Keduanya dipakai oleh elemen gelombang yang dulu memisahkan hero dari
   AddressBar. Setelah AddressBar dihapus, elemen itu jadi bg-#0f6a3b di
   atas latar bg-#0f6a3b - tak kasatmata, tapi tetap memakan sampai 160px
   ruang kosong. Elemennya sudah dihapus, jadi konstantanya ikut dibuang.

   Pemisah gelombang yang MASIH DIPAKAI adalah komponen WaveDivider di
   bawah ini (dipasang di section Layanan dan Kenapa Kami). Itu berbeda:
   ia memakai SVG berwarna, bukan mask, dan warnanya diambil dari section
   di atasnya sehingga benar-benar terlihat. */

/* ============================================================
   PEMISAH GELOMBANG ANTAR SECTION

   Kurvanya identik dengan WAVE_MASK di atas, tapi ditutup ke ATAS
   (L1440,0 L0,0 Z) alih-alih ke bawah. Artinya area yang terisi warna
   ada DI ATAS kurva, sehingga cocok dipasang di tepi atas sebuah
   section: warna section sebelumnya seolah menjorok turun.

   Kenapa SVG, bukan mask seperti WAVE_STYLE? Karena section LAYANAN
   isinya gradient + motif batik. Mask akan memotong keduanya sekaligus
   dan susah dikendalikan. Overlay SVG berwarna solid jauh lebih mudah
   ditebak hasilnya.

   Warnanya pakai currentColor, jadi diatur dari luar lewat class text-*.
   Isi dengan warna section DI ATASNYA, bukan section tempat ia dipasang.
   ============================================================ */
const WAVE_PATH =
  "M0,45 C240,100 480,10 720,45 C960,80 1200,15 1440,55 L1440,0 L0,0 Z";

function WaveDivider({ className = "", flip = false }) {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-x-0 leading-[0] ${
        flip ? "bottom-0 rotate-180" : "top-0"
      } ${className}`}
    >
      <svg
        viewBox="0 0 1440 100"
        preserveAspectRatio="none"
        className="block h-10 w-full md:h-20"
      >
        <path d={WAVE_PATH} fill="currentColor" />
      </svg>
    </div>
  );
}

/* ============================================================
   DATA
   ============================================================ */

/* SERVICES dipindah ke ServiceAccordion.jsx bersama komponennya.
   Data yang hanya dipakai satu komponen lebih baik tinggal di komponen
   itu - menyimpannya di sini membuat page.jsx terus membengkak setiap
   ada section baru. */

/* IKON VEKTOR, BUKAN BERKAS PNG.

   Sebelumnya keempatnya menunjuk ke /icons/*.png - berkas 300x300
   berisi bentuk solid kuning #f4ce09. Kuning itu MENYATU DI DALAM
   GAMBAR, jadi ia tidak ikut berubah saat tema situs diganti jadi
   hijau, dan tidak ada satu baris CSS pun yang bisa mengubahnya.

   Tiga hal yang selesai sekaligus dengan pindah ke lucide-react:

     1. Warnanya diatur dari kelas Tailwind. Ganti tema berikutnya
        tidak akan meninggalkan ikon berwarna lama.
     2. Tajam di layar Retina. PNG 300px yang ditampilkan 32px
        kehilangan detail halusnya; vektor tidak.
     3. Empat permintaan gambar hilang dari halaman.

   Seluruh bagian lain proyek ini - dropdown Header, kartu layanan,
   akordeon beranda - sudah memakai lucide. Keempat ini yang terakhir
   masih tertinggal memakai PNG.

   PEMILIHAN IKONNYA BOLEH DIGANTI. Nama lain yang masuk akal:
     kualitas   -> BadgeCheck, ScrollText
     kenyamanan -> Sofa, Coffee
     keamanan   -> Lock, ShieldCheck
     pakar      -> GraduationCap, Stethoscope
   Cukup ubah nama di import paling atas dan di daftar ini. */
const REASONS = [
  { key: "kualitas", Icon: Award },
  { key: "kenyamanan", Icon: Home },
  { key: "keamanan", Icon: ShieldCheck },
  { key: "pakar", Icon: Users },
];


const FACILITIES = [
  { key: "ot", img: "/images/ot.png" },
  { key: "ct", img: "/images/ct.png" },
  { key: "nicu", img: "/images/hicu.png" },
  { key: "icu", img: "/images/icu.png" },
];


/* ============================================================
   PAGE
   ============================================================ */

export default function Dashboard() {
  const { t } = useLanguage();

  return (
    /* Padding atas = tinggi header fixed. Header versi baru tingginya TETAP
       (tidak lagi menyusut saat scroll), jadi angka ini sekarang selalu pas:
         mobile  : 64px  = h-16 main bar (utility bar disembunyikan)
         desktop : 112px = h-10 utility bar + h-[72px] main bar
       Kalau tinggi di Header.jsx diubah, ubah angka di sini juga. */
    <div className="relative w-full overflow-x-hidden bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">
    {/* ============================================================
       SELURUH LATAR PLUM DICABUT - HALAMAN INI SEKARANG PUTIH

       Empat bidang yang tadinya berwarna:
         wrapper (di balik panel Cari Dokter)  #0f6a3b -> putih
         section Klinik Kirana                 #0f6a3b -> putih
         section Kenapa Kami                   #0f6a3b -> putih
         section Fasilitas                     #20262c -> putih

       Mengganti bg-nya saja TIDAK CUKUP. Setiap teks di keempat bidang
       itu diwarnai untuk latar gelap. Yang ikut dibalik:
         eyebrow   #bfe3cf / #7fcfa5  -> #15804a   (7,85:1 di atas putih)
         judul     text-white          -> #20262c  (15,64:1)
         paragraf  #f1faf5/90, #def2e8/80 -> slate-600
         Reason: border-white/20 -> slate-200, ring-white/15 -> black/10,
                 judul putih -> #20262c, teks -> slate-600,
                 garis hover #7fcfa5 -> #0f6a3b (di atas putih #7fcfa5
                 hanya 2,1:1, praktis tak terlihat)
      
       DUDUKAN IKON SENGAJA TETAP GELAP (#0b5231), dan sekarang
       alasannya berbeda dari sebelumnya. Dulu ia harus gelap karena
       isinya berkas PNG terang yang warnanya tidak bisa diubah.
       Ikonnya kini vektor lucide berwarna putih, jadi dudukannya
       sebenarnya bebas.

       Tetap dibiarkan gelap karena ini satu-satunya bidang gelap yang
       tersisa di badan halaman - ia memberi empat butir ini jangkar
       visual di tengah bidang putih yang panjang. Kalau nanti mau
       diterangkan, ikonnya harus ikut diubah jadi text-[var(--c-brand)].

       ============================================================
       KONSEKUENSI YANG BELUM TERSELESAIKAN - BACA INI

       1. IRAMA SECTION HILANG. Urutan halaman sekarang:
            hero -> putih -> putih -> putih -> putih -> slate-50 ->
            putih -> ContactBanner (plum) -> Footer (plum)
          Enam bidang putih beruntun terbaca sebagai SATU section
          panjang, bukan enam. Rancangan aslinya bergantung pada
          selang-seling gelap-terang untuk memberi tahu mata di mana
          satu topik berakhir. Itu sekarang tidak ada.

          Kalau perlu pemisah, pilih SALAH SATU (jangan dua-duanya):
            a. selang-seling bg-[var(--c-surface-2)] pada Kenapa Kami dan Artikel;
            b. border-t border-[var(--c-line)] di tiap <section>.

       2. GELOMBANG JADI MATI. Kedua WaveDivider sekarang text-white di
          antara dua bidang putih - tak terlihat sama sekali. Dibiarkan,
          bukan dihapus, supaya mudah dipulihkan. Keduanya absolute,
          jadi tidak memakan ruang.

       3. Facility.jsx BELUM DIPERBAIKI dan SEKARANG RUSAK. Komponen
          itu dipakai di section Fasilitas yang barusan diputihkan, dan
          keterangan fotonya memakai text-[var(--c-on-brand-2)] - merah muda sangat
          muda di atas putih, rasio sekitar 1,1:1. Praktis tidak
          terbaca. Ganti jadi text-[var(--c-ink)], dan ring-[var(--c-brand-300)]/50 di
          bingkainya jadi ring-[var(--c-brand)]/30.

       4. Panel Cari Dokter (DoctorSearch.jsx) kini putih di atas
          putih. Bayangannya masih menahan bentuknya, tapi kalau
          tepinya terasa hilang, tambahkan ring-1 ring-[var(--c-line)].

       5. const BATIK di atas berkas ini tidak dipakai di mana pun -
          sudah begitu sejak sebelum perubahan ini. Aman dihapus.
       ============================================================ */}


      {/* ========== STRIP PENGUMUMAN ==========

          DI ATAS HERO, dan itu satu-satunya posisi yang masuk akal
          untuknya. Isinya perubahan aturan yang menentukan apakah
          pasien BPJS dilayani atau dipulangkan - kalau ditaruh di
          bawah hero, pengunjung harus menggulir melewati satu layar
          penuh gambar sebelum tahu.

          TIDAK MENDORONG HERO KE BAWAH secara permanen: begitu
          tanggal `stripBerakhir` lewat, komponennya mengembalikan
          null dan tata letaknya kembali persis seperti sekarang.
          Tidak ada yang perlu dihapus dari berkas ini.

          Ia BUKAN position fixed, jadi tergulung bersama halaman.
          Disengaja - membuatnya menempel berarti mengubah pt-[64px]
          di atas dan posisi Header.jsx, dua angka yang sudah saling
          berpasangan dan gampang meleset. */}

      {/* ========== HERO - FULL BLEED ==========
          Tanpa Reveal, harus langsung terlihat karena ini elemen LCP.
          Tanpa max-width, padding, radius, dan shadow: semuanya jadi
          tidak relevan begitu gambar mentok ke tepi layar.

          Pengaturan tinggi ada di dalam HeroSlider.jsx, bukan di sini. */}
      <section className="relative w-full">
        <HeroSlider />
      </section>

      {/* ---- VARIAN LAMA: HERO BERBINGKAI ----
          Kalau nanti mau kembali ke versi yang dibatasi lebarnya:

          <section className="relative px-4">
            <div className="relative mx-auto max-w-6xl">
              <div className={`rounded-[28px] ${SHADOW_HERO}`}>
                <div className="overflow-hidden rounded-[28px] ring-1 ring-black/5">
                  <HeroSlider />
                </div>
              </div>
            </div>
          </section>
      */}

      {/* ========== CARI DOKTER ==========
          Panel menimpa bagian bawah hero dengan margin negatif.

          -mt-10 di mobile, -mt-16 mulai md. Angka ini sengaja lebih kecil
          dari tinggi panelnya: panel yang menimpa terlalu dalam akan
          menutupi tombol panah dan titik indikator hero.

          z-20 wajib. Hero punya kontrol ber-z-10 di dalamnya; tanpa z
          yang lebih tinggi, panel ini akan tertimpa oleh panah hero.

          Tanpa Reveal: panel ini elemen interaktif di paruh atas halaman,
          dan Reveal memulainya pada opacity 0. Kalau JavaScript lambat
          atau gagal, form pencarian jadi tidak terlihat sama sekali. */}
      {/* z-index diatur di dalam DoctorSearch, bukan di sini: nilainya
         berubah saat panel aktif (z-20 -> z-60, supaya naik di atas
         scrim). Kalau wrapper ini juga punya z sendiri, ia akan membuat
         stacking context baru dan panel tidak akan pernah bisa melampaui
         scrim. */}
      {/* SEBERAPA DALAM PANEL MENIMPA HERO.

         Angka negatif di bawah = kedalaman tumpang tindih. Diturunkan dari
         -mt-6 / md:-mt-20 (24px / 80px) menjadi -mt-2 / md:-mt-8
         (8px / 32px), karena panel menutupi isi poster hero.

         KALAU MASIH MENUTUPI, cukup ubah angka md:
           md:-mt-12  48px  turun sedikit
           md:-mt-8   32px  nilai sekarang
           md:-mt-4   16px  nyaris hanya menyentuh tepi
           md:mt-0     0    panel lepas penuh dari hero

         CATATAN YANG LEBIH PENTING DARI ANGKANYA:

         Pola "panel mengambang menimpa hero" dirancang untuk hero berupa
         FOTO, yang tepi bawahnya tidak membawa informasi - hanya lantai,
         langit, atau bokeh. Hero di sini poster berteks yang isinya sampai
         ke tepi bawah. Selama angkanya masih negatif, sebagian teks poster
         tetap tertutup; mengecilkannya hanya mengurangi kerusakan.

         Dua jalan yang benar-benar menyelesaikan:
         1. Pakai md:mt-0. Panel duduk penuh di bidang plum. Efek
            mengambang hilang, poster utuh.
         2. Minta desainer menyediakan area aman sekitar 120px kosong di
            bagian bawah poster. Setelah itu md:-mt-20 aman dipakai lagi.

         Kalau posternya sering berganti dan kamu tidak mengendalikan
         desainnya, opsi 1 lebih aman - ia tidak menuntut apa pun dari file
         gambar berikutnya.

         ANGKA INI BERPASANGAN dengan posisi titik indikator di
         HeroSlider.jsx (bottom-14 md:bottom-24). MENGECILKAN tumpang
         tindih selalu aman untuk titik itu; yang perlu dicek ulang adalah
         kalau nanti angkanya dibesarkan lagi. */}
      <div className="mt-4 px-4 md:mt-8 md:px-6">
        {/* max-w-7xl, disamakan dengan header section lain (Layanan Kami,
           Kenapa memilih RS JMC, Jejaring Klinik). Sebelumnya max-w-6xl,
           jadi panel ini lebih sempit dari judul-judul di bawahnya dan
           tepi kirinya tidak sejajar. */}
        <div className="mx-auto max-w-7xl">
          <DoctorSearch />
        </div>
      </div>

      {/* ========== PENGUMUMAN RESERVASI BPJS ==========

          TEPAT SETELAH CARI DOKTER, SEBELUM KLINIK KIRANA.

          Alasannya bukan sekadar "supaya terlihat". Orang yang baru
          saja memakai panel Cari Dokter adalah persis orang yang akan
          berobat - dan kalau ia peserta BPJS, cara mendaftarnya
          berubah. Informasinya datang tepat di titik ia relevan.

          KENAPA TIDAK DIGABUNG KE SLIDE HERO. Poster ini A4 tegak;
          di bingkai hero yang lebar ia pasti terpotong. Hero juga
          elemen LCP halaman ini - menambah satu gambar besar di sana
          memperlambat angka yang paling diperhatikan Google.

          ⚠️ LEBAR WADAHNYA max-w-7xl, DISAMAKAN dengan panel Cari
          Dokter di atasnya. Kalau nilai di blok atas diubah, ubah
          juga di dalam PengumumanReservasiBPJS.jsx - kalau tidak,
          tepi kiri keduanya meleset dan terlihat jelas karena
          keduanya bersebelahan langsung. */}
      <PengumumanReservasiBPJS />

      {/* ========== FASKES TINGKAT I ==========

          TIDAK LAGI FULL-BLEED. Versi lama membiarkan slider melebar
          sampai tepi layar supaya kartu tetangga bisa mengintip. Dengan
          satu kartu, lebar penuh justru bikin kartu jadi bidang putih
          raksasa di layar lebar.

          Sekarang header dan kartu memakai wadah yang sama: max-w-4xl
          (896px) dengan padding yang sama persis. Itu yang bikin tepi
          kiri judul sejajar dengan tepi kiri kartu di semua ukuran layar.
          KALAU max-w DI SINI DIUBAH, ubah juga max-w-4xl di
          ClinicSlider.jsx - kalau tidak, keduanya jadi meleset.

          UKURAN JUDUL diturunkan dari md:text-5xl ke md:text-4xl. Skala
          text-5xl dipilih dulu supaya sepadan dengan section lain yang
          selebar 1600px. Di kolom 896px, ukuran itu terbaca berteriak.

          RATA TENGAH, berbeda dari section lain yang rata kiri. Satu
          kartu tunggal yang rata kiri menyisakan ruang kosong lebar di
          kanan dan terbaca seperti daftar yang belum selesai dimuat.
          Kalau konsistensi dengan section lain lebih penting, hapus
          `text-center` dan `mx-auto` pada paragraf - keduanya tetap
          sejajar karena wadahnya sama. */}
      <section className="bg-[var(--c-surface)] px-4 pb-12 pt-12 sm:px-6 sm:pb-14 sm:pt-16 md:pb-20 md:pt-24 lg:px-10">

        <div className="mx-auto mb-8 max-w-4xl text-center sm:mb-10 md:mb-12">

          <Reveal>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:mb-3 sm:text-xs sm:tracking-[0.18em] md:text-sm">
              {t("beranda.klinikEyebrow")}
            </p>
          </Reveal>

          <Reveal delay={80}>
            <h2 className="mb-3 text-2xl font-bold leading-[1.15] tracking-tight text-[var(--c-ink)] sm:text-3xl md:mb-5 md:text-4xl">
              {t("beranda.klinikJudul")}
            </h2>
          </Reveal>

          <Reveal delay={160}>
            <p className="mx-auto max-w-2xl text-sm leading-relaxed text-[var(--c-body)] sm:text-base md:text-lg">
              {t("beranda.klinikSub")}
            </p>
          </Reveal>

        </div>

        {/* Sekarang AMAN dibungkus Reveal.

           Versi slider sengaja dibiarkan di luar Reveal karena ia mengukur
           offsetLeft tiap kartu, dan pengukuran itu bisa meleset kalau
           dilakukan saat elemennya masih di tengah transisi transform.
           Kartu tunggal tidak mengukur apa pun, jadi alasan itu gugur. */}
        <Reveal delay={240}>
          <ClinicSlider />
        </Reveal>

      </section>

      {/* ---- CATATAN TEKS ----

         Header section ini diringkas untuk SATU klinik.

         Yang dibuang dari versi sebelumnya, dan alasannya:

         - Eyebrow "Jejaring Klinik". Jejaring berarti banyak simpul.
           Dengan satu klinik, kata itu menjanjikan sesuatu yang tidak ada
           di halaman.
         - "Pilih klinik untuk melihat alamat dan kontaknya." Tidak ada
           yang bisa dipilih lagi; kalimat itu menyuruh pengunjung
           melakukan hal yang mustahil.
         - Tautan "Lihat semua jejaring klinik" ke /klinik. Selain
           menyiratkan ada banyak, route-nya sendiri masih tebakan dan
           berisiko menghasilkan 404.

         Yang tetap sengaja TIDAK ditulis:

         - "JKN Mobile" / "pindah faskes". Itu proses di aplikasi BPJS,
           bukan layanan RS. Informasinya tetap ada di dalam poster kartu
           Klinik Kirana, tempat yang lebih tepat.
         - "Rujukan terhubung langsung". Alur rujukan berjalan di sistem
           BPJS; RS tidak sepenuhnya mengendalikannya, jadi ini janji
           yang berisiko.
         - Superlatif: "terbaik", "terlengkap", "tercepat".

         CATATAN PENGULANGAN: "Klinik Kirana" dan "Faskes Tingkat I"
         sekarang muncul dua kali - di header ini, lalu diulang di dalam
         kartu. Di versi slider itu wajar karena tiap kartu harus berdiri
         sendiri. Kalau terasa mengulang, hapus baris tier dan judul h3
         di dalam ClinicCard - bukan yang di header ini, karena judul
         section lebih penting untuk pemindaian dan SEO.

         KALAU KLINIK BERTAMBAH: kembalikan eyebrow ke "Jejaring Klinik",
         judul ke "Klinik Faskes Tingkat I RS JMC", dan hidupkan lagi
         versi slider dari riwayat git.
      */}

      {/* ========== LAYANAN ==========
          DIUBAH DARI GRID KARTU JADI AKORDEON.

          Alasannya bukan sekadar gaya:
          - Grid 8 kartu memakan hampir satu layar penuh untuk menyampaikan
            8 nama layanan, dan memaksa deskripsi dipotong pendek.
          - Akordeon membiarkan judulnya dipindai cepat; detail muncul
            hanya saat dibutuhkan.

          SECTION JADI TERANG, BUKAN TEAL.
          Selain mengikuti referensi, ini memberi irama pada halaman:
          plum (Jejaring Klinik) -> terang (Layanan) -> plum (Kenapa Kami).
          Halaman yang seluruhnya satu warna terbaca datar sekalipun tiap
          section-nya rapi.

          Motif batik dan blur blob dicabut: di atas latar terang keduanya
          tidak lagi berfungsi sebagai tekstur, hanya jadi noise. */}
      <section
        id="layanan"
        className="relative scroll-mt-24 overflow-hidden bg-[var(--c-surface)] px-4 pb-16 pt-16 sm:px-6 md:scroll-mt-32 md:pb-28 md:pt-28 lg:px-10"
      >
        {/* Gelombang ini mengambil warna section DI ATASNYA. Section itu
           sekarang putih, jadi gelombangnya ikut putih dan tak terlihat.
           Dulu #0f6a3b, warna section Jejaring Klinik di
           atasnya, sehingga tampak menjorok turun ke area putih ini. */}
        <WaveDivider className="text-white" />

        {/* max-w-[1600px], bukan max-w-7xl (1280px).

           Di layar 1920px, max-w-7xl menyisakan 320px kosong di tiap sisi
           sehingga konten terkumpul di tengah dan terasa sempit. Dengan
           1600px plus px-10, marginnya jadi sekitar 160px - sedekat
           referensi ke tepi layar.

           CATATAN KONSISTENSI: section lain di halaman ini masih
           max-w-7xl. Kalau tampilan ini yang dipilih, section lain
           sebaiknya ikut diubah - lebar container yang berbeda-beda
           antar-section langsung terbaca sebagai kurang rapi. */}
        <div className="relative z-10 mx-auto max-w-[1600px]">

          {/* Judul di kiri, tautan "lihat semua" di kanan atas -
             mengikuti pola referensi. Di mobile keduanya menumpuk. */}
          <div className="mb-8 flex flex-col gap-4 md:mb-14 md:flex-row md:items-end md:justify-between md:gap-10">
            <div className="max-w-3xl">
              <Reveal>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:mb-3 sm:text-xs sm:tracking-[0.18em] md:text-sm">
                  {t("beranda.layananEyebrow")}
                </p>
              </Reveal>

              <Reveal delay={80}>
                <h2 className="mb-3 text-2xl font-bold leading-[1.1] tracking-tight text-[var(--c-ink)] sm:text-3xl md:mb-5 md:text-5xl">
                  {t("beranda.layananJudul")}
                </h2>
              </Reveal>

              <Reveal delay={160}>
                <p className="text-sm leading-relaxed text-[var(--c-body)] sm:text-base md:text-lg">
                  {t("beranda.layananSub")}
                </p>
              </Reveal>
            </div>

            <Reveal delay={220}>
              <Link
                href="/layanan"
                className="inline-flex shrink-0 items-center gap-2 text-sm font-bold text-[var(--c-brand)] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--c-brand-mid)] md:text-base"
              >
                {t("beranda.layananCta")}
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </Reveal>
          </div>

          {/* Akordeon TIDAK dibungkus Reveal.

             Reveal memulai elemen pada opacity 0. Akordeon ini interaktif,
             dan kalau JavaScript lambat atau gagal, seluruh daftar layanan
             jadi tidak terlihat sama sekali. */}
          <ServiceAccordion />

        </div>
      </section>

      {/* ========== KENAPA KAMI ==========
          DIRANCANG ULANG DARI EMPAT KOLOM SEJAJAR JADI DUA KOLOM.

          Masalah versi sebelumnya:

          1. Empat kolom sejajar memberi tiap butir lebar sekitar 300px di
             layar 1600px. Paragraf tiga baris di lebar segitu terbaca
             sempit dan berdesakan, dan judul section jadi mengambang
             sendirian di atas ruang kosong yang lebar.
          2. Garis pemisah vertikal border-white/15 di atas #0f6a3b
             kontrasnya sekitar 1.2:1 - praktis tak terlihat, jadi
             pemisahnya tidak memisahkan apa pun.
          3. Kotak ikon bg-white/10 juga nyaris tak terbaca, sehingga
             ikonnya seperti melayang tanpa dudukan.

          Sekarang: judul di kolom kiri yang menempel saat di-scroll
          (sticky), empat butir sebagai daftar bertumpuk di kanan. Ini
          memberi tiap paragraf lebar baca yang layak, dan garis
          horizontal antar butir jauh lebih mudah dilihat daripada garis
          vertikal setipis itu.

          Sticky-nya hanya aktif mulai lg. Di bawah itu kolomnya menumpuk
          dan sticky justru membuat judul menggantung aneh. */}
      <section className="relative overflow-hidden bg-[var(--c-surface)] px-4 pb-16 pt-20 sm:px-6 md:pb-24 md:pt-28 lg:px-10">

        {/* Warna gelombang = warna section DI ATASNYA (Layanan, putih). */}
        <WaveDivider className="text-white" />

        <div className="relative z-10 mx-auto max-w-[1600px]">
          {/* PEMBAGIAN DUA KOLOM DIMULAI DI xl (1280px), BUKAN lg.

             Ini memperbaiki masalah yang sudah ada sejak tadi tapi belum
             terlihat. Di lebar tepat 1024px, isi container hanya 944px.
             Dikurangi kolom judul 400px dan jarak 80px, kolom kanan cuma
             tersisa 464px - lalu dibagi dua lagi jadi sekitar 216px per
             butir. Selebar itu, "Kami senantiasa memberikan kualitas
             pelayanan terbaik dengan service excellence" pecah jadi lima
             baris.

             Di bawah xl, kolom judul dan daftar menumpuk. Daftarnya tetap
             dua kolom selebar layar, jadi tiap butir dapat sekitar 460px -
             jauh lebih lapang daripada dipaksa berdampingan dengan judul.

             LEBAR KOLOM JUDUL 480px, naik dari 400px. "Kenapa memilih RS
             Jakarta Medical Center" pada ukuran 48px membutuhkan sekitar
             960px kalau satu baris; di kolom 480px ia jatuh rapi jadi dua
             baris. Di 400px ia pecah jadi tiga, dan baris terakhirnya
             menyisakan satu kata menggantung.

             Jarak antar kolom turun dari gap-20 (80px) ke gap-16 (64px)
             untuk mengganti 80px yang diambil kolom judul. */}
          <div className="grid gap-10 xl:grid-cols-[minmax(0,480px)_1fr] xl:gap-16">

            {/* TANPA sticky. Versi sebelumnya memakai lg:sticky lg:top-32,
               yang membuat judul menempel di bawah header saat halaman
               di-scroll - akibatnya ia terpisah jauh dari daftar di
               sebelahnya dan terlihat salah rata. Dengan daftar yang kini
               dua kolom, tinggi kedua kolom hampir sama, jadi sticky tidak
               lagi memberi manfaat apa pun. */}
            <div className="xl:pt-2">
              <Reveal>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:mb-3 sm:text-xs sm:tracking-[0.18em] md:text-sm">
                  {t("beranda.kenapaEyebrow")}
                </p>
              </Reveal>

              <Reveal delay={80}>
                {/* text-balance meratakan panjang antar baris. Tanpa ini,
                   judul sepanjang ini sering jatuh jadi baris pertama yang
                   penuh dan baris kedua berisi satu-dua kata menggantung.
                   Kalau versi Tailwind-mu belum mendukungnya, class ini
                   tidak akan menghasilkan apa-apa - tidak merusak. */}
                <h2 className="mb-4 text-balance text-2xl font-bold leading-[1.1] tracking-tight text-[var(--c-ink)] sm:text-3xl md:mb-6 md:text-5xl">
                  {t("beranda.kenapaJudul")}
                </h2>
              </Reveal>

              <Reveal delay={160}>
                <p className="max-w-md text-sm leading-relaxed text-[var(--c-body)] sm:text-base md:text-lg">
                  {t("beranda.kenapaSub")}
                </p>
              </Reveal>
            </div>

            {/* DUA KOLOM, bukan satu daftar bertumpuk.

               Satu kolom membuat tiap baris selebar 1120px di layar besar,
               sementara teksnya hanya mengisi seperempatnya. Garis
               pemisah yang membentang penuh di bawah teks sependek itu
               terbaca seperti tabel yang gagal terisi.

               Dua kolom memberi tiap butir sekitar 530px - panjang baris
               yang wajar untuk dibaca - dan tinggi kolom kanan jadi
               seimbang dengan kolom judul di kirinya.

               Garis atas dipasang per butir (border-t di komponen Reason),
               bukan divide-y di sini, karena divide-y hanya menggambar
               garis di antara anak yang berurutan dan akan melewatkan
               butir pertama di kolom kedua. */}
            <div className="grid gap-x-8 sm:grid-cols-2 lg:gap-x-12 xl:gap-x-10">
              {REASONS.map((item, i) => (
                <Reveal key={item.key} delay={i * 80} className="h-full">
                  {/* KUNCI DIKIRIM SEBAGAI itemKey, BUKAN key.

                      `key` adalah nama yang dipesan React: ia dipakai
                      React untuk mencocokkan elemen antar render dan
                      TIDAK pernah diteruskan ke dalam props. Menulis
                      <Reason {...item} /> dengan item = { key, icon }
                      akan membuat Reason menerima icon saja - key-nya
                      hilang diam-diam, dan komponennya merender teks
                      kosong tanpa satu pun pesan error.

                      Karena itu properti datanya diberi nama lain saat
                      diturunkan. */}
                  <Reason itemKey={item.key} Icon={item.Icon} t={t} />
                </Reveal>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ========== FASILITAS ==========
          DIRANCANG ULANG TOTAL. Tiga hal yang diperbaiki:

          1. PANEL PEMBUNGKUS DIHAPUS.
             Versi lama memakai bg-white/5 + ring-white/10 DI ATAS latar
             #0f6a3b. Kontrasnya sekitar 1.05:1 - kotaknya ada di kode
             tapi tidak terlihat oleh mata. Yang tersisa hanya padding
             raksasa yang membuat section ini terasa kosong. Shadow di
             atas latar sewarna juga tidak menghasilkan apa-apa.

          2. LATAR JADI TEAL-900, BUKAN TEAL-600.
             Foto peralatan medis sebagian besar berwarna dingin dan
             terang. Di atas #0f6a3b keduanya bersaing. Di atas #20262c
             fotonya langsung menonjol, dan section ini jadi jangkar
             gelap di tengah halaman - irama warnanya sekarang:
             #0f6a3b (Kenapa Kami) -> #20262c (Fasilitas) -> slate-50
             (Artikel).

          3. LINGKARAN JADI KARTU PERSEGI.
             Memotong foto peralatan jadi lingkaran membuang sudut-sudut
             gambar, dan justru di sudut itulah konteks ruangannya berada.
             Kartu 4:3 mempertahankan hampir seluruh isi foto, dan
             judulnya bisa ditaruh DI ATAS gambar sehingga label dan
             gambarnya terbaca sebagai satu benda - bukan teks yang
             kebetulan berada di bawah lingkaran.

          TEKS TIDAK SAYA UBAH, hanya ditata ulang jadi rata kiri supaya
          konsisten dengan section lain. Lihat catatan di bawah section
          ini soal kata "Terkemuka". */}
      <section
        id="fasilitas"
        className="scroll-mt-24 bg-[var(--c-surface)] px-4 py-16 sm:px-6 md:scroll-mt-32 md:py-24 lg:px-10"
      >
        <div className="mx-auto max-w-[1600px]">

          <div className="mb-8 max-w-3xl sm:mb-10 md:mb-14">
            <Reveal>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:mb-3 sm:text-xs sm:tracking-[0.18em] md:text-sm">
                {t("beranda.fasilitasEyebrow")}
              </p>
            </Reveal>

            <Reveal delay={80}>
              <h2 className="mb-3 text-2xl font-bold leading-[1.1] tracking-tight text-[var(--c-ink)] sm:text-3xl md:mb-5 md:text-5xl">
                {t("beranda.fasilitasJudul1")}
              </h2>
            </Reveal>

            <Reveal delay={160}>
              <p className="text-sm leading-relaxed text-[var(--c-body)] sm:text-base md:text-lg">
                {t("beranda.fasilitasJudul2")}
              </p>
            </Reveal>
          </div>

          {/* GRID EMPAT TINGKAT.

             2 kolom  : ponsel dan tablet potret
             4 kolom  : mulai md (768px)

             Sebelumnya 4 kolom baru muncul di lg. Akibatnya pada tablet
             768-1023px lingkarannya membengkak sampai sekitar 350px dan
             menumpuk jadi dua baris yang tinggi - bentuk paling canggung
             dari section ini. Di md, empat lingkaran masing-masing
             sekitar 165px, seukuran di ponsel, dan muat satu baris.

             Jarak dinaikkan dari gap-4/5/6 ke gap-5/7/8. Bidang bundar
             butuh napas lebih banyak daripada kartu persegi: sisi
             lengkungnya sudah menyisakan ruang kosong di sudut, jadi
             jarak yang sama terasa lebih sempit daripada aslinya. */}
          <div className="grid grid-cols-2 gap-5 sm:gap-7 md:grid-cols-4 lg:gap-8">
            {FACILITIES.map((item, i) => (
              <Reveal key={item.key} delay={i * 90}>
                {/* Facility.jsx sendiri tidak perlu diubah: ia menerima
                    `title` sebagai prop, jadi cukup nilainya yang
                    diterjemahkan di sini. */}
                <Facility title={t(`beranda.fas.${item.key}`)} img={item.img} />
              </Reveal>
            ))}
          </div>

        </div>
      </section>

      {/* ---- CATATAN TEKS: FASILITAS ----

         Dua hal yang sebaiknya kamu tinjau, tapi sengaja TIDAK saya ubah
         karena ini keputusan pemasaran, bukan keputusan teknis:

         - "Teknologi Terkemuka" adalah superlatif. Sama kelasnya dengan
           "terbaik" dan "terlengkap" yang sudah kamu hindari di section
           lain. Ia juga tidak bisa dibuktikan, dan pembaca tahu itu.
         - "Terus Berkembang dan Bersaing" berbicara tentang posisi RS
           terhadap pesaing, bukan tentang apa yang pasien dapat.

         Alternatif yang lebih faktual:
           Eyebrow : Fasilitas
           Judul   : Peralatan Penunjang
           Isi     : Unit perawatan intensif dan peralatan diagnostik
                     yang tersedia di RS JMC.

         Empat nama unit di bawahnya (Operating Theatre, CT-Scan,
         NICU-PICU, ICU-ICCU) sudah menyampaikan kelasnya sendiri tanpa
         perlu dibungkus superlatif.
      */}


      {/* ========== ARTIKEL ==========
          DIUBAH DARI GRID 2x2 JADI CAROUSEL KARTU.

          Versi lama menampilkan <Article /> empat kali tanpa props, jadi
          keempat kartunya berisi artikel yang sama persis - terbaca
          sebagai situs rusak, bukan daftar artikel.

          Section jadi TERANG mengikuti referensi. Ini juga menjaga irama
          halaman: plum (Fasilitas) -> terang (Artikel) -> plum (Kontak).

          Container dan skala font disamakan dengan section Layanan dan
          Jejaring Klinik: max-w-[1600px], judul md:text-5xl. */}
      <section
        id="artikel"
        className="scroll-mt-24 bg-[var(--c-surface-2)] px-4 py-16 sm:px-6 md:scroll-mt-32 md:py-28 lg:px-10"
      >
        <div className="mx-auto max-w-[1600px]">

          <div className="mb-8 flex flex-col gap-4 md:mb-12 md:flex-row md:items-end md:justify-between md:gap-10">
            <div className="max-w-3xl">
              <Reveal>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:mb-3 sm:text-xs sm:tracking-[0.18em] md:text-sm">
                  {t("beranda.artikelEyebrow")}
                </p>
              </Reveal>

              <Reveal delay={80}>
                <h2 className="mb-3 text-2xl font-bold leading-[1.1] tracking-tight text-[var(--c-ink)] sm:text-3xl md:mb-5 md:text-5xl">
                  {t("beranda.artikelJudul")}
                </h2>
              </Reveal>

              <Reveal delay={160}>
                <p className="text-sm leading-relaxed text-[var(--c-body)] sm:text-base md:text-lg">
                  {t("beranda.artikelSub")}
                </p>
              </Reveal>
            </div>

            <Reveal delay={220}>
              <Link
                href="/edukasi-kesehatan/artikel"
                className="inline-flex shrink-0 items-center gap-2 text-sm font-bold text-[var(--c-brand)] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--c-brand-mid)] md:text-base"
              >
                {t("beranda.artikelCta")}
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </Reveal>
          </div>

          {/* Carousel TIDAK dibungkus Reveal: ia mengukur scrollWidth dan
             offsetWidth untuk menentukan posisi panah, dan pengukuran itu
             bisa meleset kalau elemennya masih di tengah transisi
             transform milik Reveal. */}
          <ArticleCarousel />

        </div>
      </section>

      {/* ========== KISAH PASIEN ==========
          Ditaruh SEBELUM Penilaian Google, bukan sesudah. Keduanya
          sama-sama bukti sosial; menaruhnya berjauhan membuat
          pengunjung menemukan bukti dua kali di dua tempat berbeda.
          Berurutan, keduanya terbaca sebagai satu blok bukti: kisah
          yang detail dulu, lalu angka agregat yang bisa diverifikasi.

          LATARNYA PLUM GELAP, satu-satunya bidang gelap yang tersisa
          di badan halaman setelah semua section diputihkan. Itu bukan
          kebetulan yang menguntungkan - bagian ini jadi satu-satunya
          jeda visual di antara enam bidang putih beruntun. Kalau
          nanti section ini dihapus, irama halaman kembali hilang.

          KOMPONENNYA MASIH MEMAKAI ISI CONTOH. Di localhost ia tampil
          dengan pita peringatan; di production ia tidak dirender sama
          sekali sampai DATA_SUDAH_ASLI di TestimonialCarousel.jsx
          disetel true. Baca kepala berkas itu sebelum menyetelnya.

          PERIKSA KE LEGAL DULU. Permenkes 1787/2010 pasal 5 melarang
          testimoni dalam publikasi pelayanan kesehatan - alasan yang
          sama yang membuat GoogleRating di bawah dibuat sebagai angka
          agregat, bukan kartu testimoni. */}
      <TestimonialCarousel />

      {/* ========== PENILAIAN GOOGLE ==========
          Diletakkan tepat setelah Artikel dan sebelum Kontak.

          Posisinya disengaja: pengunjung baru saja membaca apa yang RS
          tulis tentang dirinya sendiri, dan berikutnya adalah nomor
          telepon untuk menghubungi. Bukti dari pihak ketiga paling
          berguna tepat di antara keduanya.

          BUKAN kartu testimoni. Permenkes 1787/2010 pasal 5 melarang
          testimoni dalam publikasi pelayanan kesehatan; angka rata-rata
          yang bisa diverifikasi sendiri oleh pengunjung bukan testimoni.
          Penjelasan lengkapnya ada di kepala GoogleRating.jsx.

          LATARNYA PUTIH, di antara Artikel (slate-50) dan Kontak (plum).
          Bagian ini pendek dan berisi satu angka; latar putih dengan
          garis tipis di atas dan bawah membuatnya terbaca sebagai jeda,
          bukan sebagai section penuh yang menuntut perhatian. */}
      <GoogleRating />

      {/* KONTAK, target anchor tombol Kontak di Header.
          scroll-mt wajib karena header-nya fixed. */}
      <section id="kontak" className="scroll-mt-24 md:scroll-mt-32">
        <ContactBanner />
      </section>

      <Footer />

    </div>
  );
}

/* ============================================================
   REASON

   BENTUK dudukan ikon tetap kelopak, sama seperti kartu di Fasilitas -
   bentuk yang berulang itulah yang membuat halaman punya kosakata
   visual. Yang DIBEDAKAN adalah apa yang terjadi saat kursor mendekat.

   Kalau kamu ingin pemisahannya lebih tegas lagi, ganti KELOPAK_IKON di
   bawah dengan "rounded-2xl" - bentuknya kembali jadi persegi membulat
   dan tidak lagi berhubungan dengan section Fasilitas sama sekali.

   ============================================================
   TIGA EFEK, SEMUANYA BERBEDA DARI SECTION FASILITAS

   Di Fasilitas : sudut berpindah, garis tepi menyala, foto membesar.
   Di sini      : garis menggambar, ikon terangkat, teks bergeser.

   1. GARIS ATAS MENGGAMBAR DARI KIRI.
      Sebatang garis #7fcfa5 tumbuh menimpa garis pemisah putih yang
      sudah ada, dari kiri ke kanan, 500ms. Efek ini memakai elemen
      struktural yang memang sudah ada di sana - bukan hiasan yang
      ditambahkan khusus untuk hover.

      Dikerjakan dengan scale-x, bukan animasi lebar. Transformasi
      dijalankan kartu grafis dan tidak memaksa browser menghitung ulang
      tata letak; menganimasikan `width` memaksa perhitungan itu di
      setiap frame.

   2. DUDUKAN IKON TERANGKAT 4px dan latarnya menua jadi #20262c.
      Naik, bukan membesar - membesar adalah bahasa section Fasilitas.

   3. TEKS BERGESER 4px KE KANAN dan deskripsinya menguat dari
      #f1faf5/80 ke #f1faf5 penuh.

      Geserannya sengaja hanya 4px. Pergeseran teks yang lebih jauh
      terbaca sebagai halaman yang bergoyang, bukan sebagai umpan balik.
      Judul dan deskripsi bergeser bersama dalam satu wadah supaya
      keduanya tidak pernah tampak saling menyusul.

   TANPA cursor-pointer. Butir-butir ini bukan tautan. Efek hover boleh
   dipakai sebagai umpan balik; yang berbohong adalah kursor berbentuk
   jari, sebab ia menjanjikan bahwa klik membawa ke suatu tempat.

   BORDER-T DI KOMPONEN, BUKAN divide-y DI INDUKNYA. divide-y hanya
   menggambar garis di antara anak yang berurutan, sehingga pada grid dua
   kolom butir pertama di kolom kedua akan kehilangan garis atasnya.

   className ditulis satu baris. String class multi-baris menyelipkan
   karakter akhir baris ke dalam nilai atribut, dan pada project ber-CRLF
   itu memicu hydration error.
   ============================================================ */

const KELOPAK_IKON = "rounded-full rounded-br-none";

function Reason({ itemKey, Icon, t }) {
  return (
    <div className="group relative flex h-full gap-4 border-t border-[var(--c-line)] py-6 sm:gap-5 md:gap-6 md:py-8">

      {/* Garis yang menggambar. -top-px menaruhnya tepat menimpa
         border-t, bukan sejajar di bawahnya - kalau meleset satu piksel,
         akan terlihat dua garis bertumpuk saat hover. */}
      <span aria-hidden="true" className="absolute inset-x-0 -top-px h-px origin-left scale-x-0 bg-[var(--c-brand)] transition-transform duration-500 ease-out group-hover:scale-x-100 motion-reduce:transition-none" />

      <div className={`flex h-12 w-12 shrink-0 items-center justify-center bg-[var(--c-brand-strong)] ring-1 ring-black/10 transition-all duration-500 ease-out group-hover:-translate-y-1 group-hover:bg-[var(--c-panel)] motion-reduce:transition-none motion-reduce:group-hover:translate-y-0 sm:h-14 sm:w-14 md:h-16 md:w-16 ${KELOPAK_IKON}`}>
        {/* strokeWidth 1.75, bukan bawaan 2. Pada ikon sekecil ini
            garis 2px terbaca gemuk di dalam dudukan bundar; 1.75
            menjaganya tetap ringan tanpa jadi tipis di layar biasa.

            aria-hidden karena judul di sebelahnya sudah menyampaikan
            hal yang sama - pembaca layar tidak perlu mendengarnya
            dua kali. */}
        <Icon
          className="h-6 w-6 text-white sm:h-7 sm:w-7 md:h-8 md:w-8"
          strokeWidth={1.75}
          aria-hidden="true"
        />
      </div>

      {/* Satu wadah untuk judul dan deskripsi supaya keduanya bergeser
         serempak. Kalau digeser terpisah, selisih milidetik sekecil apa
         pun akan terlihat sebagai teks yang saling menyusul. */}
      <div className="min-w-0 pt-0.5 transition-transform duration-500 ease-out group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0 md:pt-1">
        <h3 className="mb-1.5 text-base font-bold text-[var(--c-ink)] sm:text-lg md:mb-2 md:text-xl">
          {t(`beranda.alasan.${itemKey}`)}
        </h3>

        <p className="text-sm leading-relaxed text-[var(--c-body)] transition-colors duration-500 group-hover:text-slate-900 motion-reduce:transition-none md:text-base">
          {t(`beranda.alasan.${itemKey}Teks`)}
        </p>
      </div>
    </div>
  );
}
